import React, { useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, Save, Eye, EyeOff, CheckCircle2, AlertCircle, XCircle, Key, Trash2 } from "lucide-react";

// `hintKey` names where the operator finds the credential; the dashboard paths stay in the
// dictionary with the rest of the sentence.
const META = {
  stripe: { label: "Stripe", subtitleKey: "integ.sub.stripe", hintKey: "integ.hint.stripe" },
  resend: { label: "Resend", subtitleKey: "integ.sub.resend", hintKey: "integ.hint.resend" },
  // The only supported telehealth video provider (`SUPPORTED_TELEHEALTH = ["daily"]`).
  // Its two secrets — the API key and the webhook secret — live in `integration_config`
  // together rather than split across two stores with two read paths.
  daily: { label: "Daily.co", subtitleKey: "integ.sub.daily", hintKey: "integ.hint.daily" },
  // Read at call time by `llm.py` (Claude) and `modules/social.py` (Gemini image generation).
  anthropic: { label: "Anthropic (Claude)", subtitleKey: "integ.sub.anthropic", hintKey: "integ.hint.anthropic" },
  gemini: { label: "Google Gemini", subtitleKey: "integ.sub.gemini", hintKey: "integ.hint.gemini" },
  meta_oauth: { label: "Meta", subtitleKey: "integ.sub.meta_oauth", hintKey: "integ.hint.meta_oauth" },
  linkedin_oauth: { label: "LinkedIn", subtitleKey: "integ.sub.linkedin_oauth", hintKey: "integ.hint.linkedin_oauth" },
};

/**
 * One field as GET /super-admin/integrations serves it (hq-customers-contract.md 3.3):
 *   { is_set: bool, masked: string, sensitive: bool, source: "db" | "env" | null, value?: string }
 * `value` is present for a non-sensitive key only; a secret is never read back, so whether one
 * is stored is `is_set`, and the editor sends an empty field to keep it.
 */
export default function IntegrationsEditor() {
  const t = useT("superadmin");
  const { manage: canWrite } = useScreenWrites("integrations");
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [show, setShow] = useState({});
  // Off by default, as in the API (`IntegrationsUpdate.sync_env = False`): a save already
  // persists in the database and hot-applies, and writing the server's `.env` file is a
  // deliberate, per-save choice. Defaulting it on made every one-click save rewrite the
  // checkout's `backend/.env`, from a disposable QA server included. It governs SAVES only:
  // a removal always reaches the file (see `clearField`).
  const [syncEnv, setSyncEnv] = useState(false);
  const [saving, setSaving] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoadError(null);
    api.get("/super-admin/integrations").then((r) => {
      setData(r.data);
      const initial = {};
      Object.entries(r.data.integrations || {}).forEach(([k, info]) => {
        Object.entries(info.fields).forEach(([envKey, v]) => {
          initial[envKey] = v.sensitive ? "" : (v.value ?? "");
        });
      });
      setDrafts(initial);
    }).catch(setLoadError);
  };
  useEffect(() => { load(); }, []);

  if (loadError) return <PageLoadError error={loadError} onRetry={load} testId="integrations-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  const save = async (integration) => {
    setSaving(integration);
    try {
      const keys = data.catalog[integration] || [];
      const values = {};
      keys.forEach((k) => { if (drafts[k]?.trim()) values[k] = drafts[k].trim(); });
      if (Object.keys(values).length === 0) {
        toast.error(t("integ.fillOne"));
        setSaving(null); return;
      }
      await api.put("/super-admin/integrations", { values, sync_env: syncEnv });
      toast.success(t("integ.updated", { label: META[integration].label }) + (syncEnv ? t("integ.envSynced") : ""));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("integ.error"), t));
    } finally {
      setSaving(null);
    }
  };

  // An empty value means "keep the existing one" (see `save`), so there used to be NO way
  // to remove a credential: it stayed in the DB and the startup hydration put it back into
  // os.environ on every boot. Removal has its own verb in the API (`remove`) and its own
  // button here.
  //
  // A removal always sends `sync_env: true`, whatever the toggle says. `load_dotenv` runs at
  // every start, so a key the `.env` file still binds comes back at the next restart: leaving
  // the file out would break the confirm's promise that the service stops using the key until
  // a new value is saved. The confirm names the file, so this write is never silent.
  const clearField = async (integration, envKey) => {
    if (!window.confirm(t("integ.confirmClear", { key: envKey }))) return;
    setSaving(integration);
    try {
      await api.put("/super-admin/integrations", { values: {}, remove: [envKey], sync_env: true });
      toast.success(t("integ.cleared", { key: envKey }));
      setDrafts((d) => ({ ...d, [envKey]: "" }));
      load();
    } catch (e) {
      toast.error(extractApiError(e, t("integ.error"), t));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="integrations-editor">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("integ.title")}</h1>
      <p className="text-slate-500 mb-8">{t("integ.subtitle")}</p>

      {canWrite && (
      <label className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-sol-gold/5 border border-sol-gold/20 cursor-pointer max-w-md">
        <input type="checkbox" checked={syncEnv} onChange={(e) => setSyncEnv(e.target.checked)} className="w-4 h-4" data-testid="sync-env-toggle" />
        <div>
          <div className="text-sm font-medium text-amber-600">{t("integ.syncEnvTitle")}</div>
          <div className="text-xs text-slate-500">{t("integ.syncEnvDesc")}</div>
        </div>
      </label>
      )}

      <div className="space-y-4">
        {Object.entries(data.integrations).map(([key, info]) => {
          const m = META[key] || { label: key, subtitleKey: "", hintKey: "" };
          const StatusIcon = info.configured ? CheckCircle2 : info.demo_mode ? AlertCircle : XCircle;
          const statusColor = info.configured ? "text-emerald-400" : info.demo_mode ? "text-amber-600" : "text-rose-400";
          const statusLabel = info.configured ? t("integ.statusLive") : info.demo_mode ? t("integ.statusDemo") : t("integ.statusNotConfigured");
          return (
            <div key={key} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`integ-card-${key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Key className={`w-5 h-5 ${statusColor}`} />
                  <div>
                    <div className="font-sans font-semibold text-xl">{m.label}</div>
                    <div className="text-xs text-slate-500">{m.subtitleKey ? t(m.subtitleKey) : ""}</div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-medium ${statusColor}`} data-testid={`integ-status-${key}`}>
                  <StatusIcon className="w-4 h-4" /> {statusLabel}
                </div>
              </div>

              {canWrite && m.hintKey && <div className="text-xs text-slate-400 mb-3">📍 {t(m.hintKey)}</div>}

              <div className="grid md:grid-cols-2 gap-3 mb-4">
                {(data.catalog[key] || []).map((envKey) => {
                  const field = info.fields[envKey] || {};
                  return (
                    <div key={envKey}>
                      <label className="text-xs text-slate-500 mb-1 flex items-center justify-between">
                        <code className="text-amber-600">{envKey}</code>
                        {field.is_set ? (
                          <span className="text-[10px] text-slate-400 flex items-center gap-1.5" data-testid={`current-${envKey}`}>
                            {t("integ.current")} {field.masked || "—"} <em>({field.source || "—"})</em>
                            {/* Only a value STORED in the DB can be removed: a value that comes
                                from the environment does not belong to this screen. */}
                            {canWrite && field.source === "db" && (
                              <button
                                type="button"
                                onClick={() => clearField(key, envKey)}
                                title={t("integ.clear")}
                                aria-label={t("integ.clear")}
                                className="text-slate-400 hover:text-rose-500"
                                data-testid={`clear-${envKey}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ) : !canWrite && (
                          <span className="text-[10px] text-slate-400" data-testid={`unset-${envKey}`}>{t("integ.notSet")}</span>
                        )}
                      </label>
                      {canWrite && (
                      <div className="relative">
                        <Input
                          type={field.sensitive && !show[envKey] ? "password" : "text"}
                          value={drafts[envKey] || ""}
                          onChange={(e) => setDrafts({ ...drafts, [envKey]: e.target.value })}
                          placeholder={field.is_set ? t("integ.keepExisting") : t("integ.newCredential")}
                          className="bg-white border-slate-200 pr-10"
                          data-testid={`input-${envKey}`}
                        />
                        {field.sensitive && (
                          <button
                            type="button"
                            onClick={() => setShow({ ...show, [envKey]: !show[envKey] })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800"
                            data-testid={`reveal-${envKey}`}
                          >
                            {show[envKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {canWrite && (
              <Button
                onClick={() => save(key)}
                disabled={saving === key}
                className="bg-sol-gold text-black hover:bg-amber-200"
                data-testid={`save-${key}`}
              >
                {saving === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t("integ.save")} {m.label}</>}
              </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
